import { useTranslation } from '@/contexts/i18n-context';
import { Loader } from '@/components/loaders/loader';
import styles from './person-information-section.module.css';
import clsx from 'clsx';
import {
  Gender,
  PatientRelationships,
  PreferredTitle,
  UpdateAvatarErrorResponse,
} from '@/api/api.schemas';
import { FileInput } from '../uploads/file-input';
import { useLoggedUser } from '@/contexts/current-user-context';
import { PatientImageSvg } from '../svgs/patient-image';
import { InfoRow } from '../encounter/info-row';
import { formatDate } from '@/util/formats';
import { formatName, getAge } from '@/util/helpers';
import { useCreatePatientAvatar } from '@/api/update-avatar';

interface BasePersonDetails {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  preferredTitle: PreferredTitle;
  dateOfBirth?: string | null;
  gender?: Gender | null;
  genderOther?: string | null;
}

interface PatientProps {
  variant: 'patient';
  person: BasePersonDetails;
  avatar?: Blob | null;
  isLoadingAvatar: boolean;
  onUploadPhoto: () => void;
  onError: (error?: UpdateAvatarErrorResponse) => void;
}

interface SurrogateProps {
  variant: 'surrogate';
  person: BasePersonDetails;
  relationshipToPatient: PatientRelationships;
}

type PersonDetailsSectionProps = PatientProps | SurrogateProps;

export function PersonDetailsSection(props: PersonDetailsSectionProps) {
  const { t } = useTranslation();
  const { user } = useLoggedUser();
  const { person, variant } = props;
  const isPatient = variant === 'patient';
  const { mutateAsync: updatePersonalAvatar, isPending } = useCreatePatientAvatar();

  const handleUpdateAvatar = async (files: File[]) => {
    if (files.length === 0) return;

    const avatar = files[0];
    await updatePersonalAvatar(
      { patientId: user.id, data: { avatar } },
      {
        onError: (error) => {
          if (isPatient) {
            props.onError(error);
          }
        },
      },
    );

    if (isPatient) {
      props.onUploadPhoto();
      props.onError(undefined);
    }
  };

  const getGenderOrRelationshipLabel = (): string => {
    if (!isPatient) {
      return t(`surrogate.relationshipToPatient.options.${props.relationshipToPatient}`);
    }

    if (person.gender === Gender.other) {
      return person.genderOther ?? t('common.gender.other');
    }
    return t(`common.gender.${person.gender ?? 'other'}`);
  };

  const getBirthdayLabel = (): string => {
    return `${getAge(person.dateOfBirth)} 
              ${
                getAge(person.dateOfBirth) === 1
                  ? t('patientInformation.yearOld')
                  : t('patientInformation.yearsOld')
              }`;
  };

  const getPreferredTitle = (): string => {
    return person.preferredTitle !== PreferredTitle.none
      ? ` (${t(`preferredTitle.${person.preferredTitle}`)})`
      : '';
  };

  return (
    <div className={styles.detailsContainer}>
      <div className={styles.patientInformation}>
        {isPatient && (
          <>
            {props.isLoadingAvatar ? (
              <Loader type="small" className={styles.photo} />
            ) : props.avatar ? (
              <img
                src={URL.createObjectURL(props.avatar)}
                alt={t('svg.patientAvatar')}
                className={styles.photo}
              />
            ) : (
              <PatientImageSvg className={styles.photo} />
            )}
          </>
        )}
        <div className={styles.patientName}>
          <p className="semibold">
            {formatName({
              firstName: person.firstName,
              middleName: person.middleName,
              lastName: person.lastName,
            })}
          </p>
          <InfoRow
            icon="calendar_today"
            title={t('patientInformation.birthday')}
            details={formatDate(person.dateOfBirth)}
            contentClassName={styles.birthdaySection}
          />
          <p className={clsx(styles.subtext, 'body-3')}>
            {`${getGenderOrRelationshipLabel()}${getPreferredTitle()} | ${getBirthdayLabel()}`}
          </p>
          {isPatient && (
            <FileInput
              onFilesSelected={handleUpdateAvatar}
              acceptType="images"
              buttonProps={{
                variant: 'link',
                size: 'S',
                children: t('patientInformation.patient.uploadPhoto'),
                loading: isPending,
                className: styles.uploadAvatarButton,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
